<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import { Helper } from '@/mixin/helper'
import * as NotificationApi from '@/services/rest-api-sms/lib/notification/notification'

export default {
  page: {
    title: 'Notifications',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
  },
  mixins: [Helper],
  data() {
    return {
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      totalPages: 0,
      pageOptions: [10, 25, 50, 100],
      filter: '',
      sortBy: 'created_at',
      sortDesc: true,
      isLoading: false,
      fields: [
        {
          key: 'name',
          label: 'Title',
          sortable: true,
        },
        {
          key: 'description',
          label: 'Message',
          sortable: false,
        },
        {
          key: 'username',
          label: 'User',
          sortable: true,
        },
        {
          key: 'created_at',
          label: 'Time',
          sortable: true,
        },
        {
          key: 'is_read',
          label: 'Status',
          sortable: true,
        },
      ],
    }
  },
  computed: {
    title() {
      return 'All Notifications'
    },

    notifications() {
      return this.$store.state.notification.notificationsList
    },

    unreadCount() {
      return this.$store.state.notification.unreadCount
    }
  },

  watch: {
    currentPage() {
      this.getNotifications()
    },
    perPage() {
      this.currentPage = 1
      this.getNotifications()
    },
    filter() {
      this.currentPage = 1
      this.getNotifications()
    },
  },

  mounted() {
    this.getNotifications()
  },

  methods: {
    async getNotifications() {
      this.isLoading = true

      const params = {
        filters: [`user_id eq ${this.getUserId()}`],
        search: this.filter || undefined,
        limit: this.perPage,
        page: this.currentPage,
        sorts: [`${this.sortBy} ${this.sortDesc ? 'desc' : 'asc'}`],
      }

      const res = await NotificationApi.get(params)

      this.$store.commit(
        'notification/SET_NOTIFICATION_LIST',
        res.data.paginated_result
      )

      this.totalRows = res.metadata.total_count
      this.totalPages = res.metadata.total_page

      this.isLoading = false
    },

    async markAsRead(notification) {
      const isTechnician = Number(this.$store?.state?.users?.user?.position_id) ===
        Number(process.env.VUE_APP_TECHNICIAN_POSITION_ID)

      if (!notification.wo_number) return

      if (!notification.is_read) {
        this.$store.dispatch(
          'notification/markNotificationRead',
          notification.notification_id
        )
      }

      if (isTechnician) return

      
      // this.$router.push({
      //   path: '/work-order/validation',
      //   query: {
      //     wo: notification.wo_number,
      //     t: Date.now() // force route change
      //   }
      // })

      this.$router.push({
        path: '/work-order/validation/form/' + notification.wo_number,
      })
    },

    async markAllAsRead() {
      await NotificationApi.markAsRead()
      this.$store.commit('notification/MARK_ALL_NOTIFICATIONS_READ')
      // Force update tampilan dengan refresh data dari store
      this.$forceUpdate()
      this.$toast.success('All notifications marked as read')
    },

    getNotificationType(notification) {
      if (notification.name.includes('Validasi')) return 'validation'
      if (notification.name.includes('Dimulai')) return 'info'
      return 'other'
    },

    getTypeIcon(type) {
      const icons = {
        validation: 'bx-cog',
        info: 'bx-info-circle',
        other: 'bx-file'
      }
      return icons[type] || 'bx-file'
    },
  },
}
</script>

<template>
  <Layout>
    <div class="mc-notification-page">
      <!-- Header Section -->
      <div class="mc-notification-page__header">
        <PageHeader :title="title" />
        <p class="mc-notification-page__subtitle">View and manage all your notifications</p>

        <!-- Header Actions -->
        <div class="mc-notification-page__actions">
          <div class="mc-notification-page__stats">
            <div class="mc-notification-page__stat-badge">
              <span class="mc-notification-page__stat-number">{{ unreadCount }}</span>
              <span class="mc-notification-page__stat-label">New</span>
            </div>
          </div>

          <div class="mc-notification-page__buttons">
            <b-button v-if="unreadCount > 0" variant="primary" @click="markAllAsRead"
              class="mc-notification-page__btn-mark-all">
              <i class="bx bx-check"></i>
              Mark All as Read
            </b-button>
          </div>
        </div>
      </div>

      <!-- Search Bar -->
      <div class="mc-notification-page__search">
        <div class="mc-notification-page__search-wrapper">
          <i class="bx bx-search"></i>
          <b-form-input v-model="filter" type="search" placeholder="Search notifications..."
            class="mc-notification-page__search-input" />
        </div>
      </div>

      <!-- Notifications Container -->
      <div class="mc-notification-page__container">
        <!-- Loading State -->
        <div v-if="isLoading" class="mc-notification-page__loading">
          <b-spinner class="mc-notification-page__spinner"></b-spinner>
          <p>Loading notifications...</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="notifications.length === 0" class="mc-notification-page__empty">
          <i class="bx bx-bell"></i>
          <p>No notifications</p>
        </div>

        <!-- Notifications List -->
        <div v-else class="mc-notification-list">
          <div v-for="notification in notifications" :key="notification.notification_id"
            class="mc-notification-list__item" :class="{ 'mc-notification-list__item--unread': !notification.is_read }"
            @click="markAsRead(notification)">
            <!-- Icon -->
            <div class="mc-notification-list__icon-wrapper">
              <div class="mc-notification-list__icon" :class="getNotificationType(notification)">
                <i :class="`bx ${getTypeIcon(getNotificationType(notification))}`"></i>
              </div>
            </div>

            <!-- Content -->
            <div class="mc-notification-list__content">
              <div class="mc-notification-list__header">
                <h4 class="mc-notification-list__title">
                  <i v-if="!notification.is_read" class="bx bxs-circle mc-notification-list__unread-indicator"></i>
                  {{ notification.name }}
                </h4>
              </div>

              <p class="mc-notification-list__message">
                {{ notification.description }}
              </p>

              <div class="mc-notification-list__meta">
                <span class="mc-notification-list__user">
                  <i class="bx bx-user"></i>
                  {{ notification.username }}
                </span>
                <span class="mc-notification-list__time">
                  <i class="bx bx-time"></i>
                  {{ momentDateShortMonth(notification.created_at) }} at {{ momentTime(notification.created_at) }}
                </span>
              </div>
            </div>

            <!-- Actions -->
            <div class="mc-notification-list__actions">
              <b-button v-if="!notification.is_read" variant="outline-primary" size="sm"
                @click="markAsRead(notification)"
                class="mc-notification-list__btn-action mc-notification-list__btn-read" title="Mark as read">
                <i class="bx bx-check"></i>
              </b-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination Section -->
      <div v-if="notifications.length > 0" class="mc-notification-page__pagination">
        <div class="row align-items-center">
          <div class="col-sm-12 col-md-6">
            <div class="mc-notification-page__pagination-info">
              <label class="d-inline-flex align-items-center">
                <span>Show</span>
                <b-form-select v-model="perPage" size="sm" :options="pageOptions"
                  class="mc-notification-page__pagination-select" />
                <span>entries</span>
                <span class="mc-notification-page__pagination-stats">
                  (Showing
                  <strong>
                    {{ totalRows === 0 ? 0 : (currentPage - 1) * perPage + 1 }}
                  </strong>
                  to
                  <strong>
                    {{ currentPage * perPage > totalRows ? totalRows : currentPage * perPage }}
                  </strong>
                  of
                  <strong>{{ totalRows }}</strong>
                  entries)
                </span>
              </label>
            </div>
          </div>
          <div class="col-sm-12 col-md-6">
            <div class="mc-notification-page__pagination-wrapper">
              <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" align="end" size="md" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
